import { useEffect, useRef, useState } from 'react';
import type { Contract, ContractReplacement, Milestone } from '../../types';
import {
  useStageVariationAutosave,
  VariationRow,
} from '../../hooks/useStageVariationAutosave';
import {
  canIncludeStageInVariation,
  validStageVariationAmounts,
  stageVariationMilestones,
  stageVariationOre,
} from '../../utils/stageVariation';

export const milestoneVariationRow = (
  stage: Milestone,
  sourceId?: string,
): VariationRow => ({
  key: stage.id,
  id: sourceId ? undefined : stage.id,
  sourceId,
  number: stage.milestoneNumber,
  description: stage.description,
  grossAmount: String(stage.amount),
  amount: String(stage.afterRotAmount),
  endDate: stage.endDate?.slice(0, 10) ?? '',
});
export function variationTotals(
  rows: VariationRow[],
  selectedIds: string[],
  sources: Milestone[],
) {
  const selected = sources.filter((stage) => selectedIds.includes(stage.id));
  const grossDeltaOre =
    rows.reduce((sum, row) => sum + stageVariationOre(row.grossAmount), 0) -
    selected.reduce((sum, stage) => sum + Math.round(stage.amount * 100), 0);
  const netDeltaOre =
    rows.reduce((sum, row) => sum + stageVariationOre(row.amount), 0) -
    selected.reduce(
      (sum, stage) => sum + Math.round(stage.afterRotAmount * 100),
      0,
    );
  return {
    grossDeltaOre,
    netDeltaOre,
    rotDeltaOre: grossDeltaOre - netDeltaOre,
  };
}
export function hasVariationChanges(
  rows: VariationRow[],
  selectedIds: string[],
  sources: Milestone[],
) {
  return (
    rows.some((row) => !row.sourceId) ||
    selectedIds.some((id) => {
      const source = sources.find((stage) => stage.id === id);
      const row = rows.find((item) => item.sourceId === id);
      return (
        !source ||
        !row ||
        row.description.trim() !== source.description.trim() ||
        stageVariationOre(row.grossAmount) !==
          Math.round(source.amount * 100) ||
        stageVariationOre(row.amount) !==
          Math.round(source.afterRotAmount * 100) ||
        row.endDate !== (source.endDate?.slice(0, 10) ?? '')
      );
    })
  );
}

/** Signed price-only amendments remain separate from the effective stage plan. */
export function variationProjectAdjustment(
  parent: Contract,
  contracts: Contract[],
) {
  const ancestors = new Set<string>();
  let current: Contract | undefined = parent;
  while (current && !ancestors.has(current.id)) {
    ancestors.add(current.id);
    const id: string | null | undefined =
      current.stageVariation?.originalContractId ?? current.replacesContractId;
    current = id
      ? contracts.find(
          (item) => item.id === id && item.projectId === parent.projectId,
        )
      : undefined;
  }
  return contracts
    .filter(
      (item) =>
        item.projectId === parent.projectId &&
        item.settlementType === 'price_adjustment' &&
        item.companySigned &&
        item.personSigned &&
        (item.versionStatus ?? 'current') === 'current' &&
        item.parentContractId &&
        ancestors.has(item.parentContractId),
    )
    .reduce(
      (sum, item) => sum + Math.round(Number(item.afterRotAmount) * 100),
      0,
    );
}

export function useVariationPlan(
  settlement: ContractReplacement | null,
  sources: Milestone[],
  onSaved: (result: ContractReplacement) => Promise<void>,
  onBeforeChange?: (id: string) => Promise<boolean>,
  readOnly = false,
) {
  const [rows, setRows] = useState<VariationRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [checking, setChecking] = useState(false);
  const [grossOffset, setGrossOffset] = useState(0);
  const loaded = useRef<string | null>(null);
  const plan = useRef<Milestone[]>([]);
  const changing = useRef(false);
  useEffect(() => {
    if (!settlement) {
      loaded.current = null;
      setRows([]);
      setSelectedIds([]);
      return;
    }
    if (loaded.current === settlement.replacementContractId) return;
    loaded.current = settlement.replacementContractId;
    const scope = settlement.amendmentScope!;
    plan.current = settlement.milestones ?? [];
    const nextRows: VariationRow[] = stageVariationMilestones(
      settlement,
      plan.current,
    ).map((stage) => ({
      ...milestoneVariationRow(stage),
      sourceId: stage.replacesMilestoneId,
      restoresMilestoneId: scope.restoredMilestones?.find(
        (link) => link.milestoneId === stage.id,
      )?.sourceMilestoneId,
    }));
    const nextIds = scope.milestones.map((stage) => stage.id);
    if (scope.newStageRequested)
      nextRows.push({
        key: 'requested-stage',
        description: '',
        grossAmount: '',
        amount: '',
        endDate: '',
        sourceId: null,
      });
    setGrossOffset(
      (settlement.grossDeltaOre ?? 0) -
        variationTotals(nextRows, nextIds, sources).grossDeltaOre,
    );
    setRows(nextRows);
    setSelectedIds(nextIds);
    setShowErrors(false);
  }, [settlement, sources]);
  const totals = variationTotals(rows, selectedIds, sources);
  totals.grossDeltaOre += grossOffset;
  totals.rotDeltaOre += grossOffset;
  const valid =
    Number.isSafeInteger(totals.grossDeltaOre) &&
    Number.isSafeInteger(totals.netDeltaOre) &&
    rows.every(
      (row) =>
        row.description.trim() &&
        validStageVariationAmounts(stageVariationOre(row.grossAmount), stageVariationOre(row.amount)),
    );
  const autosave = useStageVariationAutosave({
    contractId: settlement?.replacementContractId ?? '',
    draft: {
      rows,
      selectedIds,
      grossDelta: String(totals.grossDeltaOre / 100),
    },
    valid,
    readOnly: !settlement || readOnly,
    initiallyPending: false,
    onBound: (bind, savedPlan) => {
      plan.current = savedPlan;
      setRows((previous) => bind(previous));
    },
    onSaved,
  });
  const changeSource = async (source: Milestone, remove: boolean) => {
    if (changing.current || !canIncludeStageInVariation(source)) return;
    changing.current = true;
    setChecking(true);
    try {
      if (
        !selectedIds.includes(source.id) &&
        onBeforeChange &&
        !(await onBeforeChange(source.id))
      )
        return;
      setSelectedIds((current) =>
        current.includes(source.id) ? current : [...current, source.id],
      );
      setRows((current) =>
        remove
          ? current.filter((row) => row.sourceId !== source.id)
          : current.some((row) => row.sourceId === source.id)
            ? current
            : [
                ...current,
                {
                  ...milestoneVariationRow(source, source.id),
                  id: plan.current.find(
                    (stage) => stage.replacesMilestoneId === source.id,
                  )?.id,
                },
              ],
      );
    } finally {
      changing.current = false;
      setChecking(false);
    }
  };
  return {
    rows,
    selectedIds,
    totals,
    valid,
    showErrors,
    checking,
    autosave,
    changed: hasVariationChanges(rows, selectedIds, sources),
    edit: (source: Milestone) => changeSource(source, false),
    omit: (source: Milestone) => changeSource(source, true),
    undo: (sourceId: string) => {
      setSelectedIds((current) => current.filter((id) => id !== sourceId));
      setRows((current) => current.filter((row) => row.sourceId !== sourceId));
    },
    patch: (key: string, patch: Partial<VariationRow>) =>
      setRows((current) =>
        current.map((row) => (row.key === key ? { ...row, ...patch } : row)),
      ),
    remove: (key: string) =>
      setRows((current) => current.filter((row) => row.key !== key)),
    add: () =>
      setRows((current) => [
        ...current,
        {
          key: crypto.randomUUID(),
          description: '',
          grossAmount: '',
          amount: '',
          endDate: '',
        },
      ]),
    restore: (source: Milestone) =>
      setRows((current) =>
        current.some((row) => row.restoresMilestoneId === source.id)
          ? current
          : [
              ...current,
              {
                key: crypto.randomUUID(),
                restoresMilestoneId: source.id,
                description: source.description,
                grossAmount: String(source.amount),
                amount: String(source.afterRotAmount),
                endDate: '',
              },
            ],
      ),
    validate: async () => {
      setShowErrors(true);
      return (
        valid &&
        hasVariationChanges(rows, selectedIds, sources) &&
        (await autosave.flush())
      );
    },
  };
}
export type VariationPlan = ReturnType<typeof useVariationPlan>;
